import { Box, CircularProgress } from '@mui/material'
import { ColDef, GridReadyEvent } from 'ag-grid-community'
import { getQueryById } from 'api/query'
import InfinityAgGrid from 'components/table/InfinityAgGrid'
import { UIModal } from 'features/UI'
import { SyntheticEvent, useCallback, useMemo, useState } from 'react'
import { CareArticleType, QueryRange } from 'types'

interface LinkedArticleRow {
  range: string
  link: string
  type: string
}

function getCareArticleTypeLabel(careArticleType?: string | null): string {
  if (careArticleType === CareArticleType.Task) return 'Action Post'
  return 'Guide Post'
}

function buildRows(resultRanges: QueryRange[]): LinkedArticleRow[] {
  const rows: LinkedArticleRow[] = []

  for (const range of resultRanges) {
    const rangeLabel = `${range.pointsFrom} - ${range.pointsTo}`

    if (range.learnArticleId && range.learnArticleName) {
      rows.push({
        range: rangeLabel,
        link: range.learnArticleName,
        type: 'Learn library Article',
      })
    }

    if (range.careArticleId && range.careArticleName) {
      rows.push({
        range: rangeLabel,
        link: range.careArticleName,
        type: getCareArticleTypeLabel(range.careArticleType),
      })
    }
  }

  return rows
}

export function QueryLinkedArticlesCell({ queryId, count }: { queryId: number; count: number }) {
  const [isOpen, setIsOpen] = useState(false)
  const [rowData, setRowData] = useState<LinkedArticleRow[]>([])
  const [loading, setLoading] = useState(false)

  const handleOpen = useCallback(
    async (event: SyntheticEvent) => {
      event.preventDefault()
      setIsOpen(true)
      setLoading(true)
      try {
        const response = await getQueryById({ id: queryId })
        setRowData(buildRows(response.data.resultRanges))
      } finally {
        setLoading(false)
      }
    },
    [queryId],
  )

  const handleClose = () => {
    setIsOpen(false)
  }

  const handleGridReady = (event: GridReadyEvent) => {
    event.api.sizeColumnsToFit()
  }

  const columnDefs: ColDef[] = useMemo(() => {
    return [
      {
        headerName: 'Range from - Range to',
        field: 'range',
      },
      {
        headerName: 'Link',
        field: 'link',
      },
      {
        headerName: 'Type',
        field: 'type',
      },
    ]
  }, [])

  return (
    <div>
      <a href="" onClick={handleOpen}>
        {count}
      </a>

      <UIModal isOpen={isOpen} onClose={handleClose} width={748} title="Linked Articles">
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" height={400}>
            <CircularProgress />
          </Box>
        ) : (
          <div
            className="ag-theme-alpine"
            style={{ height: '400px', width: '700px', fontSize: '1rem' }}
          >
            <InfinityAgGrid
              rowData={rowData}
              columnDefs={columnDefs}
              rowModelType="clientSide"
              onGridReady={handleGridReady}
              floatingFiltersHeight={0}
            />
          </div>
        )}
      </UIModal>
    </div>
  )
}
